import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createOrganization, getOrganizationsByTenantId, updateOrganizationMetadata } from "./api";
import { useBoundStore } from "store";
import { IOrganizationMetadata } from "./models";

export const organizationKeys = {
  all: ["organizations"] as const,
  list: (tenantId?: string) =>
    [...organizationKeys.all, tenantId ?? "unknown"] as const,
};

export function useOrganizations(
  tenantId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: organizationKeys.list(tenantId),
    queryFn: () => getOrganizationsByTenantId(tenantId as string),
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Creates a new organization for the current tenant.
 */
export function useCreateOrganizationMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: (data: { name: string; description?: string; projectPrefix?: string }) =>
      createOrganization(tenant!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.list(tenant?.id) });
    },
  });
}

/**
 * Updates the current organization's metadata.
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization, setCurrentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: (metadata: IOrganizationMetadata) =>
      updateOrganizationMetadata(tenant!.id, currentOrganization!.id, metadata),
    onSuccess: (data) => {
      setCurrentOrganization(data);
      queryClient.invalidateQueries({ queryKey: organizationKeys.list(tenant?.id) });
    },
  });
}
